import { SegmentedControlOption } from "../components/SegmentedControl/types";
import { WindowType } from "./types";

export const COLORS_GLOBAL = {
  black: "#000000",
  white: "#ffffff",
  border: "#e1e8ed",
  primaryColorBackground: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  golden: "#a8691b",

  // Gray scale
  gray50: "#151a1e",
  gray100: "#2b333b",
  gray200: "#364049",
  gray300: "#5c6670",
  gray400: "#68737d",
  gray500: "#75808a",
  gray600: "#7e8c9a",
  gray700: "#9ca8b4",
  gray800: "#b9c4d0",
  gray900: "#d9e0e8",
  gray1000: "#f2f5f7",
  grayIcon: "#bdbdbd",
  grayBorder: "#d1d5db",
  grayBackground: "#f9fafb",
  grayPill: "#f4f1ea",

  // Brand colors
  blue: "#42cbf5",
  blue2: "#3b82f6",
  blue3: "#2563eb",
  blue4: "#60a5fa",
  yellow: "#f5f542",
  yellow2: "#fef3c7",
  yellow3: "#fde68a",
  yellow4: "#fde047",
  yellow5: "#a16207",
  yellow6: "#92400e",
  purple: "#a64dff",

  // Additional UI colors
  lightGray: "#f0f0f0",
  lightGray2: "#e0e0e0",
  lightGray3: "#f8f8f8",
  lightGray4: "#f5f7fa",
  lightGray5: "#c3cfe2",
  lightGray6: "#e5e7eb",
  lightGray7: "#f9fafb",
  lightGray8: "#d4d4d4",
  lightGray9: "#f8f9fa",
  mediumGray: "#757575",
  mediumGray2: "#666",
  mediumGray3: "#7f8c8d",
  darkGray: "#dadce0",
  darkGray2: "#34495e",

  // `success`/`error` are darkened to clear the WCAG AA 4.5:1 text ratio on white surfaces (dark theme overrides these).
  success: "#15803D",
  success2: "#10b981",
  success3: "#22c55e",
  success4: "#16a34a",
  success5: "#059669",
  success6: "#166534",
  successHover: "#77DF9B",
  successBackground: "#EAF7EE",
  successBackground2: "#dcfce7",
  successBackground3: "#e8f5e8",
  successBorder: "#CBE9D4",

  info: "#006DDD",
  info2: "#3498DB",
  info3: "#3b82f6",
  infoBackground: "#E5EFFA",
  infoBackground2: "#e3f2fd",
  infoBackground3: "#eff6ff",
  infoBorder: "#BAD8F0",
  infoBorder2: "#bfdbfe",

  warning: "#F1931B",
  warning2: "#f59e0b",
  warning3: "#d97706",
  warning4: "#fed7aa",
  warningHover: "#F8C471",
  warningBackground: "#FFF7EA",
  warningBackground2: "#fefce8",
  warningBorder: "#F8DEBB",

  error: "#CF3520",
  error2: "#922B21",
  error3: "#ef4444",
  error4: "#dc2626",
  error5: "#fecaca",
  errorHover: "#f2715a",
  errorBackground: "#FDEDE9",
  errorBackground2: "#fdeaea",
  errorBackground3: "#fef2f2",
  errorBorder: "#F5C7BD",
  errorBorder2: "#fadbd8",
  errorBorder3: "#fee2e2",

  activeBorder: "#4caf50",
  activeColor: "#2e7d32",

  // Text colors
  textPrimary: "#1f2937",
  textSecondary: "#5f6673",
  textMuted: "#111827",
};

// ─── Light Theme A: Verdigris ────────────────────────────────────────────────
// Deep mineral teal primary. Cool blue-gray body. Teal gradient sidebar.
const COLORS_THEME_WHITE_VERDIGRIS = {
  backgroundBody: "#f0efe9",
  backgroundCard: "#ffffff",
  backgroundBodyDisabled: "#d8dde6",
  backgroundShimmer: "#dce8e5",

  table: {
    headerBackground: "#1a2535",
    headerColor: "#4ecdb8",
    headerHover: "#72ddc9",
    headerActive: "#96edda",
  },

  card: {
    background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
    backgroundTransparent: "rgba(255, 255, 255, 0.5)",
    border: "#dce4ee",
    metricBackground: "rgba(26, 117, 104, 0.04)",
    metricBorder: "rgba(26, 117, 104, 0.08)",
  },

  navbar: {
    background: "linear-gradient(180deg, #1a7568 0%, #0d4d45 100%)",
    shadow: "rgba(0, 0, 0, 0.1)",
    headerColor: "#ffffff",
    headerBorder: "rgba(255, 255, 255, 0.1)",
    iconBackground: "rgba(255, 255, 255, 0.2)",
    userSettings: "#ffffff",
    toggleBackground: "#ffffff",
    toggleColor: "#1a7568",
    toggleShadow: "rgba(0, 0, 0, 0.15)",
    toggleHover: "#f0f8f7",
    toggleMobileBackground: "rgba(26, 117, 104, 0.9)",
    toggleMobileColor: "#ffffff",
    toggleMobileHover: "rgba(26, 117, 104, 1)",
    activeUnderline: "#7de0cf",
  },

  payment: {
    dateChipBg: "rgba(26, 117, 104, 0.12)",
    statusCompletedBg: "rgba(16, 185, 129, 0.08)",
    statusCompletedColor: "#059669",
    statusPendingBg: "rgba(245, 158, 11, 0.08)",
    statusPendingColor: "#d97706",
    statusFailedBg: "rgba(239, 68, 68, 0.08)",
    statusFailedColor: "#dc2626",
  },

  calendar: {
    hoverBackground: "rgba(26, 117, 104, 0.06)",
    disabledBackground: "rgba(26, 117, 104, 0.1)",
  },

  tab: {
    background: "#f7f5ef",
  },

  primary: "#1a7568",
  primaryHover: "#218f7f",

  secondary: "#2d5f8a",
  secondaryHover: "#3a6d9e",

  ternary: "#1f3545",

  button: {
    cancelColor: "#1f2937",
    cancelBackground: "#ffffff",
    cancelBorder: "#1f2937",
    cancelHoverBackground: "#f5f1e6",
    cancelHoverBorder: "#bfb59c",
    cancelActiveBackground: "#ede7d6",
    cancelActiveBorder: "#a89e85",
  },

  primaryColorBackground: "linear-gradient(135deg, #1a7568 0%, #0d7a8a 100%)",
  gradientPrimary: "linear-gradient(135deg, #218f7f 0%, #1a7568 100%)",
  gradientPrimary2: "linear-gradient(135deg, #1a7568 0%, #0d7a8a 100%)",
  shadowButton: "0 6px 20px rgba(26, 117, 104, 0.5)",

  hover: "rgba(26, 117, 104, 0.08)",

  negativeColor: "rgba(239, 68, 68, 0.15)",
  negativeColorBorder: "#ef4444",

  positiveColor: "rgba(26, 117, 104, 0.15)",
  positiveColorBorder: "#1a7568",

  heroBackground:
    "linear-gradient(135deg, #f3f7f6 0%, #e6f0ee 60%, #d8e8e5 100%)",
  heroFooterBackground: "rgba(31, 53, 69, 0.05)",

  detailIconBackground: "linear-gradient(135deg, #1a7568 0%, #0d7a8a 100%)",

  hoverBackground: "rgba(0, 0, 0, 0.02)",

  tooltipBackground: "#1f2937",
  tooltipText: "#ffffff",
  tooltipBorder: "rgba(0, 0, 0, 0.2)",

  border: "#dce2ec",
  borderLight: "#eaf0f6",

  tabHoverBackground: "rgba(26, 117, 104, 0.08)",

  infoBoxBackground: "#e3f2fd",
  infoBoxBorder: "#90caf9",
  infoBoxText: "#1976d2",

  toggleGroupBackground: "#f0f4f8",
  toggleActiveBackground: "#ffffff",
  toggleHoverBackground: "rgba(26, 117, 104, 0.06)",
  toggleActiveShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",

  skeletonGradient:
    "linear-gradient(90deg, #e4eeec 25%, #d8e8e6 50%, #e4eeec 75%)",
  skeletonGradient2:
    "linear-gradient(90deg, #e4eeec 0%, #f0f6f5 20%, #e4eeec 40%, #e4eeec 100%)",
  skeletonGradient3:
    "linear-gradient(90deg, rgba(0, 0, 0, 0.06) 0%, rgba(0, 0, 0, 0.1) 50%, rgba(0, 0, 0, 0.06) 100%)",

  statusSuccess: "#059669",
  statusSuccessHover: "#10b981",
  statusSuccessBackground: "rgba(16, 185, 129, 0.08)",
  statusSuccessBackgroundSoft: "rgba(16, 185, 129, 0.04)",
  statusSuccessBorder: "rgba(16, 185, 129, 0.3)",
  statusSuccessBorderSoft: "rgba(16, 185, 129, 0.15)",

  statusError: "#dc2626",
  statusErrorHover: "#ef4444",
  statusErrorBackground: "rgba(239, 68, 68, 0.08)",
  statusErrorBackgroundSoft: "rgba(239, 68, 68, 0.04)",
  statusErrorBorder: "rgba(239, 68, 68, 0.3)",
  statusErrorBorderSoft: "rgba(239, 68, 68, 0.15)",

  statusWarning: "#d97706",
  statusWarningHover: "#f59e0b",
  statusWarningBackground: "rgba(245, 158, 11, 0.08)",
  statusWarningBackgroundSoft: "rgba(245, 158, 11, 0.04)",
  statusWarningBorder: "rgba(245, 158, 11, 0.3)",
  statusWarningBorderSoft: "rgba(245, 158, 11, 0.15)",

  statusInfo: "#1976d2",
  statusInfoHover: "#2196f3",
  statusInfoBackground: "rgba(25, 118, 210, 0.08)",
  statusInfoBackgroundSoft: "rgba(25, 118, 210, 0.04)",
  statusInfoBorder: "rgba(25, 118, 210, 0.3)",
  statusInfoBorderSoft: "rgba(25, 118, 210, 0.15)",

  focusShadow: "0 0 0 3px rgba(26, 117, 104, 0.25)",
  focusShadow2: "0 0 0 3px rgba(26, 117, 104, 0.2)",
  focusShadow3: "0 0 0 4px rgba(26, 117, 104, 0.15)",

  emptyStateBackground:
    "linear-gradient(135deg, rgba(26, 117, 104, 0.04) 0%, rgba(13, 122, 138, 0.04) 100%)",
  emptyStateIconBackground: "linear-gradient(135deg, #c8ede8 0%, #b8e4f0 100%)",
  emptyStateIconColor: "#1a7568",
  emptyStateBorder: "rgba(26, 117, 104, 0.15)",

  backgroundOverlay: "rgba(0, 0, 0, 0.3)",

  notificationSuccessText: "#166534",
  notificationSuccessBorder: "#22c55e",
  notificationSuccessBackgroundStart: "rgba(255, 255, 255, 0.98)",
  notificationSuccessBackgroundEnd: "rgba(240, 253, 244, 0.98)",

  notificationInfoText: "#1e40af",
  notificationInfoBorder: "#3b82f6",
  notificationInfoBackgroundStart: "rgba(255, 255, 255, 0.98)",
  notificationInfoBackgroundEnd: "rgba(239, 246, 255, 0.98)",

  notificationWarningText: "#92400e",
  notificationWarningBorder: "#f59e0b",
  notificationWarningBackgroundStart: "rgba(255, 255, 255, 0.98)",
  notificationWarningBackgroundEnd: "rgba(254, 252, 232, 0.98)",

  notificationErrorText: "#991b1b",
  notificationErrorBorder: "#ef4444",
  notificationErrorBackgroundStart: "rgba(255, 255, 255, 0.98)",
  notificationErrorBackgroundEnd: "rgba(254, 242, 242, 0.98)",
};

// ─── Dark Theme: Ember ───────────────────────────────────────────────────────
// Very dark charcoal body. Amber accent. Dark slate sidebar. Steel blue secondary.
const COLORS_THEME_DARK_EMBER = {
  backgroundBody: "#111318",
  backgroundCard: "#1d2025",
  backgroundBodyDisabled: "#191c20",
  backgroundShimmer: "#25282c",

  table: {
    headerBackground: "#0d0f14",
    headerColor: "#c9891e",
    headerHover: "#dba235",
    headerActive: "#edba50",
  },

  card: {
    background: "linear-gradient(135deg, #1f2226 0%, #1b1e22 100%)",
    backgroundTransparent: "rgba(27, 30, 34, 0.5)",
    border: "rgba(255, 255, 255, 0.1)",
    metricBackground: "rgba(255, 255, 255, 0.05)",
    metricBorder: "rgba(255, 255, 255, 0.08)",
  },

  navbar: {
    background: "linear-gradient(180deg, #1e2028 0%, #14161c 100%)",
    shadow: "rgba(0, 0, 0, 0.4)",
    headerColor: "rgba(255, 255, 255, 0.9)",
    headerBorder: "rgba(255, 255, 255, 0.08)",
    iconBackground: "rgba(255, 255, 255, 0.08)",
    userSettings: "rgba(255, 255, 255, 0.9)",
    toggleBackground: "rgba(255, 255, 255, 0.1)",
    toggleColor: "#c9891e",
    toggleShadow: "rgba(0, 0, 0, 0.3)",
    toggleHover: "rgba(255, 255, 255, 0.06)",
    toggleMobileBackground: "rgba(201, 137, 30, 0.95)",
    toggleMobileColor: "#ffffff",
    toggleMobileHover: "rgba(201, 137, 30, 1)",
  },

  payment: {
    dateChipBg: "rgba(201, 137, 30, 0.15)",
    statusCompletedBg: "rgba(52, 211, 153, 0.15)",
    statusCompletedColor: "#34d399",
    statusPendingBg: "rgba(251, 191, 36, 0.15)",
    statusPendingColor: "#fbbf24",
    statusFailedBg: "rgba(248, 113, 113, 0.15)",
    statusFailedColor: "#f87171",
  },

  calendar: {
    hoverBackground: "rgba(201, 137, 30, 0.15)",
    disabledBackground: "rgba(201, 137, 30, 0.08)",
  },

  tab: {
    background: "#1b1e22",
  },

  primary: "#c9891e",
  primaryHover: "#dea130",

  secondary: "#4a7095",
  secondaryHover: "#5a85ab",

  ternary: "#e0e4ea",

  button: {
    cancelColor: "#e6e8ea",
    cancelBackground: "#262119",
    cancelBorder: "#3a342a",
    cancelHoverBackground: "#2f291f",
    cancelHoverBorder: "#4a4234",
    cancelActiveBackground: "#262119",
    cancelActiveBorder: "#3a342a",
  },

  primaryColorBackground: "linear-gradient(135deg, #9d6b1a 0%, #c9891e 100%)",
  gradientPrimary: "linear-gradient(135deg, #c9891e 0%, #dea130 100%)",
  gradientPrimary2: "linear-gradient(135deg, #9d6b1a 0%, #c9891e 100%)",
  shadowButton: "0 6px 20px rgba(201, 137, 30, 0.5)",

  hover: "rgba(255, 255, 255, 0.05)",

  negativeColor: "rgba(248, 113, 113, 0.15)",
  negativeColorBorder: "#f87171",

  positiveColor: "rgba(52, 211, 153, 0.15)",
  positiveColorBorder: "#34d399",

  textPrimary: "#e6e8ea",
  textSecondary: "#92a0b0",
  textMuted: "#6e7a88",
  textDisabled: "#92a0b0",

  heroBackground:
    "linear-gradient(135deg,  rgba(74, 112, 149, 0.12) 100%, rgba(201, 137, 30, 0.12) 0%, rgba(137, 112, 89, 0.10) 60%)",
  heroFooterBackground: "rgba(255, 255, 255, 0.03)",
  detailIconBackground: "linear-gradient(135deg, #c9891e 0%, #dea130 100%)",

  hoverBackground: "rgba(255, 255, 255, 0.03)",

  tooltipBackground: "revert",
  tooltipText: "#1b1e28",
  tooltipBorder: "revert",

  border: "rgba(255, 255, 255, 0.1)",
  borderLight: "rgba(255, 255, 255, 0.06)",

  tabHoverBackground: "rgba(201, 137, 30, 0.1)",

  infoBoxBackground: "rgba(96, 165, 250, 0.12)",
  infoBoxBorder: "rgba(96, 165, 250, 0.3)",
  infoBoxText: "#93c5fd",

  toggleGroupBackground: "#191c20",
  toggleActiveBackground: "#252a2c",
  toggleHoverBackground: "rgba(201, 137, 30, 0.08)",
  toggleActiveShadow: "0 2px 8px rgba(201, 137, 30, 0.25)",

  skeletonGradient:
    "linear-gradient(90deg, rgba(255, 255, 255, 0.03) 25%, rgba(255, 255, 255, 0.08) 50%, rgba(255, 255, 255, 0.03) 75%)",
  skeletonGradient2:
    "linear-gradient(90deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0.06) 20%, rgba(255, 255, 255, 0.03) 40%, rgba(255, 255, 255, 0.03) 100%)",
  skeletonGradient3:
    "linear-gradient(90deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.05) 50%, rgba(255, 255, 255, 0.02) 100%)",

  statusSuccess: "#34d399",
  statusSuccessHover: "#6ee7b7",
  statusSuccessBackground: "rgba(52, 211, 153, 0.15)",
  statusSuccessBackgroundSoft: "rgba(52, 211, 153, 0.08)",
  statusSuccessBorder: "rgba(52, 211, 153, 0.3)",
  statusSuccessBorderSoft: "rgba(52, 211, 153, 0.2)",

  statusError: "#f87171",
  statusErrorHover: "#fca5a5",
  statusErrorBackground: "rgba(248, 113, 113, 0.15)",
  statusErrorBackgroundSoft: "rgba(248, 113, 113, 0.08)",
  statusErrorBorder: "rgba(248, 113, 113, 0.3)",
  statusErrorBorderSoft: "rgba(248, 113, 113, 0.2)",

  statusWarning: "#fbbf24",
  statusWarningHover: "#fcd34d",
  statusWarningBackground: "rgba(251, 191, 36, 0.15)",
  statusWarningBackgroundSoft: "rgba(251, 191, 36, 0.08)",
  statusWarningBorder: "rgba(251, 191, 36, 0.3)",
  statusWarningBorderSoft: "rgba(251, 191, 36, 0.2)",

  statusInfo: "#60a5fa",
  statusInfoHover: "#93c5fd",
  statusInfoBackground: "rgba(96, 165, 250, 0.15)",
  statusInfoBackgroundSoft: "rgba(96, 165, 250, 0.08)",
  statusInfoBorder: "rgba(96, 165, 250, 0.3)",
  statusInfoBorderSoft: "rgba(96, 165, 250, 0.2)",

  focusShadow: "0 0 0 3px rgba(201, 137, 30, 0.4)",
  focusShadow2: "0 0 0 3px rgba(201, 137, 30, 0.3)",
  focusShadow3: "0 0 0 4px rgba(201, 137, 30, 0.25)",

  emptyStateBackground: "rgba(201, 137, 30, 0.05)",
  emptyStateIconBackground:
    "linear-gradient(135deg, rgba(201, 137, 30, 0.15) 0%, rgba(74, 112, 149, 0.12) 100%)",
  emptyStateIconColor: "#d4941f",
  emptyStateBorder: "rgba(201, 137, 30, 0.2)",

  backgroundOverlay: "rgba(0, 0, 0, 0.6)",

  notificationSuccessText: "#4ade80",
  notificationSuccessBorder: "#16a34a",
  notificationSuccessBackgroundStart: "rgba(20, 83, 45, 0.95)",
  notificationSuccessBackgroundEnd: "rgba(21, 128, 61, 0.95)",

  notificationInfoText: "#60a5fa",
  notificationInfoBorder: "#2563eb",
  notificationInfoBackgroundStart: "rgba(30, 58, 138, 0.95)",
  notificationInfoBackgroundEnd: "rgba(37, 99, 235, 0.95)",

  notificationWarningText: "#fbbf24",
  notificationWarningBorder: "#d97706",
  notificationWarningBackgroundStart: "rgba(120, 53, 15, 0.95)",
  notificationWarningBackgroundEnd: "rgba(180, 83, 9, 0.95)",

  notificationErrorText: "#fca5a5",
  notificationErrorBorder: "#dc2626",
  notificationErrorBackgroundStart: "rgba(127, 29, 29, 0.95)",
  notificationErrorBackgroundEnd: "rgba(185, 28, 28, 0.95)",
};

// ─── Active Themes ────────────────────────────────────────────────────────────
// Light: switch to COLORS_THEME_WHITE_VERDIGRIS (default) — deep teal sidebar.
export const COLORS_THEME_WHITE = COLORS_THEME_WHITE_VERDIGRIS;

// Dark: switch to COLORS_THEME_DARK_GRAPHITE to restore the original violet theme.
export const COLORS_THEME_DARK = COLORS_THEME_DARK_EMBER;

export const GRAPH_COLORS = [
  "#22d3ee",
  "#f59e0b",
  "#3b82f6",
  "#10b981",
  "#6366f1",
  "#ff6384",
  "#a855f7",
  "#14b8a6",
  "#ef4444",
  "#8b5cf6",
  "#f97316",
  "#06b6d4",
  "#84cc16",
  "#ec4899",
  "#0ea5e9",
];

// Box shadows
export const SHADOWS = {
  small: "0 1px 3px rgba(0, 0, 0, 0.1)",
  small2: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
  medium: "0 2px 8px rgba(0, 0, 0, 0.08)",
  medium2: "0 2px 4px rgba(0, 0, 0, 0.1)",
  medium3:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
  large: "0 4px 20px rgba(0, 0, 0, 0.08)",
  large2: "0 8px 30px rgba(0, 0, 0, 0.12)",
  large3: "0 8px 30px rgba(0, 0, 0, 0.15)",
  xlarge: "0 20px 40px rgba(0, 0, 0, 0.1)",
  focus4: "0 0 0 4px rgba(37, 99, 235, 0.1)",
  dropdown: "0 8px 24px rgba(0, 0, 0, 0.15)",
  hover: "0 4px 12px rgba(0, 0, 0, 0.1)",
  active: "0 2px 8px rgba(220, 38, 38, 0.3)",
};

// Gradient backgrounds
export const GRADIENTS = {
  skeleton4:
    "linear-gradient(90deg, transparent 25%, rgba(255, 255, 255, 0.4) 50%, transparent 75%)",
  skeleton5:
    "linear-gradient(90deg, transparent 0%, rgba(0, 0, 0, 0.03) 50%, transparent 100%)",
  authBackground: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
  authOverlay: "linear-gradient(135deg, #667eea20, #764ba220)",
  card: "linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%)",
  cardGreen: "linear-gradient(135deg, #e8f5e8 0%, #f8f9fa 100%)",
  cardRed: "linear-gradient(135deg, #fdeaea 0%, #f8f9fa 100%)",
  cardBlue: "linear-gradient(135deg, #e3f2fd 0%, #f8f9fa 100%)",
  warning: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
  error: "linear-gradient(135deg, #fdeaea 0%, #ffffff 100%)",
  deactivated: "linear-gradient(135deg, #f5f6f8 0%, #e9ecf1 58%, #dce0e7 100%)",
  deactivatedDark:
    "linear-gradient(135deg, #2b2f36 0%, #23272e 58%, #1b1f26 100%)",
};

// Opacity overlays and backgrounds
export const OVERLAYS = {
  backdrop: "rgba(0, 0, 0, 0.7)",
  backdropLight: "rgba(0, 0, 0, 0.5)",
  sidebarOverlay: "rgba(0, 0, 0, 0.5)",
  tooltipBg: "rgba(0, 0, 0, 0.9)",
  white95: "rgba(255, 255, 255, 0.95)",
  white90: "rgba(255, 255, 255, 0.9)",
  white70: "rgba(255, 255, 255, 0.7)",
  white50: "rgba(255, 255, 255, 0.5)",
  white25: "rgba(255, 255, 255, 0.25)",
  white20: "rgba(255, 255, 255, 0.2)",
  white15: "rgba(255, 255, 255, 0.15)",
  white10: "rgba(255, 255, 255, 0.1)",
  white08: "rgba(255, 255, 255, 0.08)",
  white05: "rgba(255, 255, 255, 0.05)",
  black08: "rgba(0, 0, 0, 0.08)",
  black05: "rgba(0, 0, 0, 0.05)",
  black04: "rgba(0, 0, 0, 0.04)",
  black03: "rgba(0, 0, 0, 0.03)",
  black02: "rgba(0, 0, 0, 0.02)",
  blackBorder: "rgba(0, 0, 0, 0.1)",
  hover: "rgba(0, 0, 0, 0.05)",
  hoverWhite: "rgba(255, 255, 255, 0.1)",
  blue05: "rgba(59, 130, 246, 0.05)",
  white30: "rgba(255, 255, 255, 0.3)",
  white40: "rgba(255, 255, 255, 0.4)",
  white60: "rgba(255, 255, 255, 0.6)",
  white85: "rgba(255, 255, 255, 0.85)",
  black10: "rgba(0, 0, 0, 0.1)",
  black40: "rgba(0, 0, 0, 0.4)",
};

export const WEIGHTS = {
  normal: 400,
  medium: 550,
  bold: 700,
};

// Typography scale. Steps chosen to absorb the existing rem clusters with
// minimal shift; 0.75rem (12px) is the legible floor for micro-labels.
// Consumed as CSS vars (--text-*) injected in GlobalStyles, mirroring WEIGHTS.
export const FONT_SIZES = {
  xs: "0.75rem", // 12px - uppercase micro-labels (floor)
  sm: "0.8125rem", // 13px - secondary / caption text
  base: "0.875rem", // 14px - default body / metric value
  md: "1rem", // 16px
  lg: "1.125rem", // 18px
  xl: "1.25rem", // 20px - compact headings
  xxl: "1.5rem", // 24px - section values
  xxxl: "1.875rem", // 30px - card titles
  display: "2.1rem", // ~34px - hero / balance display
  displayLg: "2.5rem", // 40px - large hero / display
  displayXl: "3rem", // 48px - oversized hero / illustration
};

// "The One Face Rule": Crimson Pro carries every typographic scale, from display
// headings down to uppercase labels. Loaded in index.html; Georgia is the
// metric-near serif fallback so a slow web font swap never shifts to a sans.
export const FONTS = {
  serif: "'Crimson Pro', Georgia, 'Times New Roman', serif",
};

export const BREAKPOINTS = {
  mobile: 440,
  tabletMin: 550,
  tablet: 700,
  tabletLarge: 850,
  snapshotSplit: 1001,
  laptopMin: 1100,
  lg: 1200,
  laptopLarge: 1400,
};

export const QUERIES = {
  lg: `(min-width: 1200px)`,
  tabletAndDown: `(max-width: ${BREAKPOINTS.tabletMin / 16}rem)`,
  tabletAndUp: `(min-width: ${BREAKPOINTS.tabletMin / 16}rem)`,
  tabletLargeAndDown: `(max-width: ${(BREAKPOINTS.tabletLarge - 1) / 16}rem)`,
  tabletLargeAndUp: `(min-width: ${BREAKPOINTS.tabletLarge / 16}rem)`,
  snapshotSplitAndUp: `(min-width: ${BREAKPOINTS.snapshotSplit / 16}rem)`,
  snapshotSplitAndDown: `(max-width: ${(BREAKPOINTS.snapshotSplit - 1) / 16}rem)`,
  laptopDown: `(max-width: ${(BREAKPOINTS.laptopMin - 1) / 16}rem)`,
  laptopAndUp: `(min-width: ${BREAKPOINTS.laptopMin / 16}rem)`,
  tabletOnly: `(min-width: ${BREAKPOINTS.tabletMin / 16}rem) and (max-width: ${(BREAKPOINTS.laptopMin - 1) / 16}rem)`,
  laptopLargeAndUp: `(min-width: ${BREAKPOINTS.laptopLarge / 16}rem)`,
};

// 8px grid; xxs is a hairline step below the grid floor for icon/label gaps and tight badge padding.
export const SPACING = {
  xxs: 2,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

// WCAG 2.5.8 minimum; an icon glyph or short label alone is far smaller than a thumb.
export const TOUCH_TARGET = 44;

// Per-breakpoint spacing: mobile minimizes margins, desktop is more generous.
export const SPACING_RESPONSIVE = {
  mobile: {
    pageMargin: SPACING.sm + SPACING.xs,
    cardGridPadding: SPACING.sm + SPACING.xs,
    cardGridGap: SPACING.sm + SPACING.xs,
    cardPadding: SPACING.md,
    chartPadding: SPACING.md,
    chartMargin: SPACING.sm + SPACING.xs,
    sectionGap: SPACING.md,
  },
  tablet: {
    pageMargin: SPACING.md,
    cardGridPadding: SPACING.md,
    cardGridGap: SPACING.md,
    cardPadding: SPACING.lg - SPACING.xs,
    chartPadding: SPACING.lg - SPACING.xs,
    chartMargin: SPACING.md,
    sectionGap: SPACING.lg - SPACING.xs,
  },
  desktop: {
    pageMargin: SPACING.lg,
    cardGridPadding: SPACING.lg - SPACING.xs,
    cardGridGap: SPACING.lg - SPACING.xs,
    cardPadding: SPACING.lg,
    chartPadding: SPACING.lg,
    chartMargin: SPACING.lg - SPACING.xs,
    sectionGap: SPACING.lg,
  },
} as const;

// No bounce or elastic easings — they feel dated and undermine precision.
export const ANIMATION = {
  // Duration values
  duration: {
    instant: "0.1s",
    fast: "0.15s",
    normal: "0.2s",
    moderate: "0.3s",
    slow: "0.4s",
    slower: "0.5s",
  },

  // Easing functions
  easing: {
    linear: "linear",
    easeIn: "ease-in",
    easeOut: "ease-out",
    easeInOut: "ease-in-out",
    // Exponential ease-outs — preferred for all entering elements and transforms
    easeOutQuart: "cubic-bezier(0.25, 1, 0.5, 1)",
    easeOutQuint: "cubic-bezier(0.22, 1, 0.36, 1)",
    easeOutExpo: "cubic-bezier(0.16, 1, 0.3, 1)",
    // Material Design curve vocabulary
    sharp: "cubic-bezier(0.4, 0, 0.6, 1)",
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
    decelerate: "cubic-bezier(0, 0, 0.2, 1)",
    accelerate: "cubic-bezier(0.4, 0, 1, 1)",
  },

  // Common combined values for quick usage
  transitions: {
    all: "all 0.2s ease-out",
    allFast: "all 0.15s ease-out",
    allSlow: "all 0.3s ease-out",
    color:
      "color 0.2s ease-out, background-color 0.2s ease-out, border-color 0.2s ease-out",
    transform: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    fade: "opacity 0.3s ease-out",
    slide: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease-out",
  },

  // Animation delays for staggered effects
  delay: {
    none: "0s",
    short: "0.05s",
    medium: "0.1s",
    long: "0.15s",
  },
} as const;

export const THEMES = {
  dark: "dark",
  light: "light",
};

export const FILTER_OPTIONS: SegmentedControlOption<WindowType>[] = [
  { label: "YTD", value: WindowType.YTD },
  { label: "1Y", value: WindowType.Y1 },
  { label: "3Y", value: WindowType.Y3 },
  { label: "5Y", value: WindowType.Y5 },
  { label: "All", value: WindowType.ALL },
];

export const BASE_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8081";
export const PAGE_SIZE = 10;
export const SEARCH_DEBOUNCE_MS = 350;
export const TOKEN = import.meta.env.VITE_TOKEN ?? "TiQkXYS42wp8vgw";
export const ID = import.meta.env.VITE_ID ?? "IxKPSkB5ZtddVEt";
export const NAME = import.meta.env.VITE_NAME ?? "NxZoRsq02BSl9cr";
export const COOKIES_CONCENT =
  import.meta.env.VITE_COOKIES_CONCENT ?? "ClDdC1ActSpmIwb";
export const COOKIES_THEME =
  import.meta.env.VITE_COOKIES_THEME ?? "sTkd291HsdEd6M2E";

// Helper function to get theme-specific color
export const getThemeColors = (theme: string) => {
  return theme === THEMES.dark ? COLORS_THEME_DARK : COLORS_THEME_WHITE;
};

// Public landing page: approved Polar White / Verdigris composition.
export const COLORS_WELCOME = {
  deep: "#0d4d45",
  muted: "#596675",
  soft: "#e8f3ef",
  onDeep: "#d1e5df",
} as const;
